/**
 * TextInput component - Accessible text input for conversation.
 *
 * Design: DENiZEN — rounded, warm, nature-inspired
 * - Rounded container with white background
 * - Circular send/mic buttons
 * - WCAG 2.1 AA compliant
 *
 * @security Voice input uses backend transcription to keep API credentials
 * secure. See issue #75.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useSpeechToText, type TranscribeFunction } from '@/hooks/useSpeechToText'

export interface TextInputProps {
  onSubmit: (message: string, imageData?: string) => void
  placeholder?: string
  disabled?: boolean
  isLoading?: boolean
  maxLength?: number
  showCharCount?: boolean
  ariaLabel?: string
  isGeneratingImages?: boolean
  transcribeAudio?: TranscribeFunction
}

export function TextInput({
  onSubmit,
  placeholder = 'Share your thoughts...',
  disabled = false,
  isLoading = false,
  maxLength = 2000,
  showCharCount = true,
  ariaLabel = 'Your response',
  isGeneratingImages = false,
  transcribeAudio,
}: TextInputProps) {
  const [value, setValue] = useState('')
  const [speechAutoStopMessage, setSpeechAutoStopMessage] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Hold-to-speak tracking
  const pressStartTimeRef = useRef<number | null>(null)
  const autoContinueTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isAutoContinue, setIsAutoContinue] = useState(false)
  const [isHolding, setIsHolding] = useState(false)
  const AUTO_CONTINUE_THRESHOLD_MS = 5000
  const MIN_RECORDING_DURATION_MS = 500

  const {
    isListening,
    transcript,
    interimTranscript,
    error: speechError,
    isSupported: isSpeechSupported,
    isTranscribing,
    startListening,
    stopListening,
    cancelListening,
    resetTranscript,
  } = useSpeechToText({
    transcribe: transcribeAudio,
  })

  const [shortRecordingMessage, setShortRecordingMessage] = useState<string | null>(null)

  const isListeningRef = useRef(isListening)
  useEffect(() => {
    isListeningRef.current = isListening
  }, [isListening])

  const clearAutoContinueTimeout = useCallback((): void => {
    if (autoContinueTimeoutRef.current) {
      clearTimeout(autoContinueTimeoutRef.current)
      autoContinueTimeoutRef.current = null
    }
  }, [])

  const resetHoldState = useCallback((): void => {
    pressStartTimeRef.current = null
    setIsAutoContinue(false)
    setIsHolding(false)
    clearAutoContinueTimeout()
  }, [clearAutoContinueTimeout])

  const safeStopListening = useCallback((context: string): void => {
    try {
      void stopListening()
    } catch (err: unknown) {
      console.error('[TextInput] stopListening failed:', {
        error: err instanceof Error ? err.message : String(err),
        errorType: err instanceof Error ? err.name : typeof err,
        operation: 'stopListening',
        context,
        wasListening: isListeningRef.current,
      })
    }
  }, [stopListening])

  useEffect(() => {
    return () => {
      if (autoContinueTimeoutRef.current) {
        clearTimeout(autoContinueTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (speechError && pressStartTimeRef.current !== null) {
      console.info('[TextInput] Resetting hold state due to speech error:', {
        error: speechError,
        wasHolding: isHolding,
        wasAutoContinue: isAutoContinue,
      })
      resetHoldState()
    }
  }, [speechError, resetHoldState, isHolding, isAutoContinue])

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [])

  useEffect(() => {
    adjustHeight()
  }, [value, adjustHeight])

  useEffect(() => {
    if (transcript) {
      setValue((prev) => {
        const separator = prev && !prev.endsWith(' ') ? ' ' : ''
        return prev + separator + transcript
      })
      resetTranscript()
    }
  }, [transcript, resetTranscript])

  useEffect(() => {
    if (isGeneratingImages && isListening) {
      console.info('[TextInput] Auto-stopping speech recognition for image generation')
      safeStopListening('image-generation-auto-stop')
      setSpeechAutoStopMessage('Voice input paused for image generation')
      const timeout = setTimeout(() => setSpeechAutoStopMessage(null), 3000)
      return () => clearTimeout(timeout)
    }
    return undefined
  }, [isGeneratingImages, isListening, safeStopListening])

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value
    if (newValue.length <= maxLength) {
      setValue(newValue)
    }
  }

  const handleMicMouseDown = () => {
    if (isSpeechDisabled) return
    pressStartTimeRef.current = Date.now()
    setIsAutoContinue(false)
    setIsHolding(true)
    clearAutoContinueTimeout()

    startListening().catch((err: unknown) => {
      console.error('[TextInput] Failed to start listening:', {
        error: err instanceof Error ? err.message : String(err),
        errorType: err instanceof Error ? err.name : typeof err,
        operation: 'startListening',
        wasHolding: true,
        wasAutoContinue: false,
      })
    })

    autoContinueTimeoutRef.current = setTimeout(() => {
      if (pressStartTimeRef.current !== null && isListeningRef.current) {
        setIsAutoContinue(true)
      }
    }, AUTO_CONTINUE_THRESHOLD_MS)
  }

  const handleMicMouseUp = () => {
    if (isSpeechDisabled) return
    clearAutoContinueTimeout()

    const holdDuration = pressStartTimeRef.current
      ? Date.now() - pressStartTimeRef.current
      : 0

    pressStartTimeRef.current = null
    setIsHolding(false)

    if (!isAutoContinue && isListening) {
      if (holdDuration < MIN_RECORDING_DURATION_MS) {
        console.info('[TextInput] Recording too short, cancelling:', holdDuration, 'ms (min:', MIN_RECORDING_DURATION_MS, 'ms)')
        cancelListening()
        setShortRecordingMessage('Hold longer while speaking')
        setTimeout(() => setShortRecordingMessage(null), 2500)
        return
      }
      safeStopListening('mouse-up')
    }
  }

  const handleMicClick = () => {
    if (isAutoContinue && isListening) {
      safeStopListening('tap-to-stop')
      setIsAutoContinue(false)
    }
  }

  const handleMicMouseLeave = () => {
    if (pressStartTimeRef.current !== null && !isAutoContinue && isListening) {
      const holdDuration = Date.now() - pressStartTimeRef.current
      if (holdDuration < MIN_RECORDING_DURATION_MS) {
        cancelListening()
        setShortRecordingMessage('Hold longer while speaking')
        setTimeout(() => setShortRecordingMessage(null), 2500)
      } else {
        safeStopListening('mouse-leave')
      }
      clearAutoContinueTimeout()
    }
    pressStartTimeRef.current = null
    setIsHolding(false)
  }

  const handleSubmit = () => {
    const trimmedValue = value.trim()
    if (trimmedValue && !disabled && !isLoading) {
      onSubmit(trimmedValue)
      setValue('')
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const isDisabled = disabled || isLoading
  const isSpeechDisabled = isDisabled || isGeneratingImages || isTranscribing
  const hasSpeechSupport = isSpeechSupported && !!transcribeAudio
  const charCount = value.length
  const isNearLimit = charCount > maxLength * 0.9
  const canSubmit = !isDisabled && value.trim().length > 0

  return (
    <div className="relative">
      {/* Input container — rounded, white bg */}
      <div
        className={`
          flex items-end gap-2 p-4 rounded-[9px] border border-dark-green/20 bg-white
          ${isDisabled ? 'opacity-70' : ''}
        `}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={isListening && interimTranscript ? interimTranscript : placeholder}
          disabled={isDisabled}
          rows={1}
          className="flex-1 resize-none bg-transparent font-body text-xl text-dark-green focus:outline-none disabled:cursor-not-allowed"
          aria-label={ariaLabel}
          aria-describedby={showCharCount ? 'char-count' : undefined}
        />

        {/* Microphone button — rounded */}
        {hasSpeechSupport && (
          <div className="relative group">
            <button
              type="button"
              onMouseDown={handleMicMouseDown}
              onMouseUp={handleMicMouseUp}
              onMouseLeave={handleMicMouseLeave}
              onClick={handleMicClick}
              onTouchStart={(e) => { e.preventDefault(); handleMicMouseDown() }}
              onTouchEnd={(e) => { e.preventDefault(); handleMicMouseUp() }}
              onTouchCancel={handleMicMouseLeave}
              disabled={isSpeechDisabled}
              className={`flex-shrink-0 w-10 h-10 rounded-[9px] flex items-center justify-center transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green focus-visible:ring-offset-1 ${
                isSpeechDisabled
                  ? 'bg-gray-100 cursor-not-allowed'
                  : (isHolding || isListening)
                    ? 'bg-dark-green text-white'
                    : 'bg-white border border-dark-green/40 text-dark-green hover:bg-teal/10'
              }`}
              style={{
                animation: isListening ? 'pulse-recording 1s ease-in-out infinite' : 'none',
              }}
              aria-label={
                isListening
                  ? (isAutoContinue ? 'Tap to stop recording' : 'Release to stop recording')
                  : 'Hold to speak'
              }
              aria-pressed={isListening}
              aria-disabled={isSpeechDisabled}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
              </svg>
            </button>
            {/* Tooltip */}
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-white rounded-lg shadow-denizen-sm font-body text-xs text-dark-green whitespace-nowrap opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none transition-opacity">
              {isListening
                ? (isAutoContinue ? 'Tap to stop' : 'Release to stop')
                : 'Hold to speak'}
            </span>
          </div>
        )}

        {/* Submit button — rounded */}
        <div className="relative group">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`flex-shrink-0 w-10 h-10 rounded-[9px] flex items-center justify-center transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green focus-visible:ring-offset-1 ${
              canSubmit
                ? 'bg-light-green border border-dark-green text-dark-green hover:brightness-95'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            aria-label="Send message"
          >
            {isLoading ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            )}
          </button>
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-white rounded-lg shadow-denizen-sm font-body text-xs text-dark-green whitespace-nowrap opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none transition-opacity">
            Send message (or press Enter)
          </span>
        </div>
      </div>

      {/* Speech error or status */}
      {speechError && (
        <div className="mt-2 px-3 py-2 text-xs font-body bg-error/10 text-dark-green rounded-xl" role="alert">
          {speechError}
        </div>
      )}
      {(isListening || isTranscribing) && !speechError && (
        <div
          className="absolute left-0 right-0 bottom-full mb-2 px-3 py-2 text-xs font-body bg-dark-green text-white rounded-xl z-10"
          role="status"
          aria-live="polite"
        >
          {isTranscribing ? 'Transcribing...' : 'Recording...'} {interimTranscript && `"${interimTranscript}"`}
        </div>
      )}
      {speechAutoStopMessage && !isListening && (
        <div
          className="mt-2 px-3 py-2 text-xs font-body bg-light-green/50 text-dark-green rounded-xl animate-fade-in"
          role="status"
          aria-live="polite"
        >
          {speechAutoStopMessage}
        </div>
      )}
      {shortRecordingMessage && !isListening && (
        <div
          className="mt-2 px-3 py-2 text-xs font-body bg-warning/20 text-dark-green rounded-xl animate-fade-in"
          role="status"
          aria-live="polite"
        >
          {shortRecordingMessage}
        </div>
      )}

      {/* Footer row */}
      <div className="flex items-center justify-between mt-2 px-1">
        <p className="text-sm font-body text-muted">Press ENTER to send</p>

        {showCharCount && (
          <span
            id="char-count"
            className={`text-sm font-body ${isNearLimit ? 'text-error' : 'text-muted'}`}
            aria-live="polite"
          >
            {charCount}/{maxLength}
          </span>
        )}
      </div>
    </div>
  )
}

export default TextInput
